// Version: 1.3
// v1.3: Dead stage colour (pipeline Dead lead bucket, CV-54).
// v1.2: Buttons use the shared button system (CV-49): 8px corners, standard variants and sizes, one selected look; the old btnPrimary, btnSmall, btnDanger, iconBtn and chip style objects are gone. Panels still drawn dark carry className="surface-dark" so their buttons pick up dark values.
// v1.1: Secondary buttons use the app theme tokens (surface/text/border) so
//        they are readable on the light page as well as the dark cards —
//        the old translucent light-grey style was near-invisible in the
//        pipeline toolbar.
// 1.0: Shared helpers for the Sales area (SalesSearch.js, Pipeline.js):
//      API wrapper using the same CSRF pattern as Tickets.js (localStorage
//      'stg_csrf' as X-CSRF-Token, credentials included), file download for
//      CSV/XLSX export, stage colours, shared styles, Modal and Badge.
import { useState, useEffect } from "react";

export function useIsMobile(breakpoint = 768) {
  const [mobile, setMobile] = useState(window.innerWidth <= breakpoint);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);
  return mobile;
}

function csrfHeaders() {
  const token = localStorage.getItem("stg_csrf");
  return token ? { "X-CSRF-Token": token } : {};
}

// Returns parsed JSON. Throws an Error with .status and .data on failure.
export async function api(path, { method = "GET", body } = {}) {
  const opts = { method, credentials: "include", headers: { ...csrfHeaders() } };
  if (body !== undefined) {
    opts.headers["Content-Type"] = "application/json";
    opts.body = JSON.stringify(body);
  }
  const res = await fetch(path, opts);
  let data = null;
  try { data = await res.json(); } catch { data = null; }
  if (!res.ok) {
    const err = new Error((data && data.error) || `Request failed (${res.status})`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

// POST and save the response as a file (used for CSV and XLSX export).
export async function downloadPost(path, body, fallbackName) {
  const res = await fetch(path, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json", ...csrfHeaders() },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let msg = `Export failed (${res.status})`;
    try { const d = await res.json(); msg = d.error || msg; } catch {}
    throw new Error(msg);
  }
  const cd = res.headers.get("Content-Disposition") || "";
  const m = cd.match(/filename="?([^"]+)"?/);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = m ? m[1] : fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export function fmtUpdated(iso) {
  if (!iso) return "";
  const d = new Date(/Z$|[+-]\d\d:\d\d$/.test(iso) ? iso : iso + "Z");
  if (isNaN(d)) return "";
  return d.toLocaleString("en-GB", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Europe/London",
  }) + " GMT";
}

export function fmtDate(ymd) {
  if (!ymd) return "";
  const d = new Date(ymd + "T12:00:00Z");
  if (isNaN(d)) return ymd;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", timeZone: "Europe/London" });
}

export function ago(iso) {
  if (!iso) return "";
  const d = new Date(/Z$|[+-]\d\d:\d\d$/.test(iso) ? iso : iso + "Z");
  const diff = Math.floor((Date.now() - d) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export function money(v) {
  const n = Number(v) || 0;
  return "£" + n.toLocaleString("en-GB", { maximumFractionDigits: 0 });
}

export function todayYmd() {
  return new Date().toLocaleDateString("en-CA", { timeZone: "Europe/London" });
}

export const STAGE_META = {
  New:         { color: "#86868b", bg: "rgba(134,134,139,0.12)" },
  Researching: { color: "#64d2ff", bg: "rgba(100,210,255,0.10)" },
  Contacted:   { color: "#00d4ff", bg: "rgba(0,212,255,0.10)" },
  Engaged:     { color: "#bf5af2", bg: "rgba(191,90,242,0.10)" },
  Proposal:    { color: "#ff9f0a", bg: "rgba(255,159,10,0.10)" },
  Won:         { color: "#30d158", bg: "rgba(48,209,88,0.10)" },
  Lost:        { color: "#ff453a", bg: "rgba(255,69,58,0.10)" },
  Dead:        { color: "#8e8e93", bg: "rgba(99,99,102,0.18)" },
};

export function Badge({ label, color = "#86868b", bg, title }) {
  return (
    <span title={title} style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "2px 8px", borderRadius: 20,
      background: bg || "rgba(134,134,139,0.12)", color,
      fontSize: 11, fontWeight: 600, letterSpacing: "0.03em", whiteSpace: "nowrap",
    }}>{label}</span>
  );
}

export function Modal({ title, onClose, children, footer, maxWidth = 520 }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 1100,
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 16, backdropFilter: "blur(4px)",
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="surface-dark" style={{
        background: "#1c1c1e", borderRadius: 16, width: "100%", maxWidth,
        border: "1px solid #2c2c2e", boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
        maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden",
      }}>
        <div style={{ padding: "18px 22px", borderBottom: "1px solid #2c2c2e", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#f5f5f7", minWidth: 0, overflowWrap: "anywhere" }}>{title}</h2>
          <button className="btn btn-quiet btn-icon" style={{ flexShrink: 0 }} onClick={onClose} aria-label="Close">×</button>
        </div>
        <div style={{ padding: "18px 22px", overflow: "auto", display: "flex", flexDirection: "column", gap: 14 }}>
          {children}
        </div>
        {footer && (
          <div style={{ padding: "14px 22px 18px", borderTop: "1px solid #2c2c2e", display: "flex", justifyContent: "flex-end", gap: 10, flexWrap: "wrap" }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export function ErrorBox({ children }) {
  if (!children) return null;
  return (
    <div style={{ background: "rgba(255,45,85,0.1)", border: "1px solid rgba(255,45,85,0.3)", borderRadius: 8, padding: "10px 14px", color: "#ff2d55", fontSize: 13 }}>
      {children}
    </div>
  );
}

export function StatCard({ label, value, color, sub }) {
  return (
    <div className="surface-dark" style={{ background: "#1c1c1e", border: "1px solid #2c2c2e", borderRadius: 12, padding: "14px 16px", minWidth: 0 }}>
      <div style={{ fontSize: 11, color: "#86868b", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: color || "#f5f5f7", lineHeight: 1.2, marginTop: 2 }}>{value ?? "—"}</div>
      {sub && <div style={{ fontSize: 11, color: "#86868b", marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export const pageStyle = (isMobile) => ({
  padding: isMobile ? "16px 12px" : "24px 28px", maxWidth: 1400, margin: "0 auto",
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", boxSizing: "border-box",
});

export const labelStyle = {
  display: "block", fontSize: 11, fontWeight: 600, color: "#86868b",
  letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 5,
};

export const inputStyle = {
  width: "100%", boxSizing: "border-box", background: "#2c2c2e",
  border: "1px solid #3a3a3c", borderRadius: 8, padding: "9px 12px",
  color: "#f5f5f7", fontSize: 13, outline: "none", fontFamily: "inherit",
};

export const btnSecondary = {
  background: "var(--surface, #fff)", color: "var(--text, #1d1d1f)", border: "1px solid var(--border, #c7c7cc)",
  borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
};

export const sectionTitle = {
  fontSize: 11, color: "#86868b", fontWeight: 600, letterSpacing: "0.06em",
  textTransform: "uppercase", margin: "0 0 8px",
};
